import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Zap, TrendingUp, MapPin, Euro } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const EuropeTruckArticle = () => {
  const { t } = useLanguage();

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="container mx-auto px-6 py-8">
        <Button 
          variant="ghost" 
          onClick={() => window.history.back()}
          className="mb-6 hover:bg-gray-100"
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Back to Industry Highlights
        </Button>

        <article className="max-w-4xl mx-auto">
          <header className="mb-8">
            <div className="flex items-center gap-4 mb-4">
              <Badge className="bg-green-500 text-white">
                <Zap className="h-3 w-3 mr-1" />
                Electric Mobility
              </Badge>
              <span className="text-gray-500 text-sm">November 2025</span>
            </div>
            
            <h1 className="text-4xl font-bold text-gray-900 mb-4">
              Europe's E-Truck Market Diverges: Netherlands Leads BEV Adoption Revolution
            </h1>
            
            <p className="text-xl text-gray-600 leading-relaxed">
              Regional differences emerge as European electric heavy truck adoption accelerates, 
              with the Netherlands achieving remarkable 11.2% BEV market share in Q3 2025.
            </p>
          </header>

          <div className="mb-8">
            <img 
              src="https://d1dcnte8mfzkpv.cloudfront.net/uploads/2024/09/1b8bd9dfdeec192713b40e1d93d1.jpg" 
              alt="Electric truck charging infrastructure and fleet operations" 
              className="w-full h-64 object-cover rounded-lg shadow-lg"
            />
          </div>

          <div className="grid md:grid-cols-3 gap-6 mb-8">
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <MapPin className="h-5 w-5 mr-2 text-green-600" />
                  Netherlands
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-green-600">11.2%</div>
                <p className="text-sm text-gray-600">BEV share Q3 2025</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <TrendingUp className="h-5 w-5 mr-2 text-blue-600" />
                  EU Average
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-3xl font-bold text-blue-600">1.8%</div>
                <p className="text-sm text-gray-600">Q1-Q3 2025 BEV share</p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center">
                  <Euro className="h-5 w-5 mr-2 text-purple-600" />
                  TCO Status
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-lg font-bold text-purple-600">Approaching</div>
                <p className="text-sm text-gray-600">Parity with diesel</p>
              </CardContent>
            </Card>
          </div>

          <div className="prose prose-lg max-w-none">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Market Divergence Analysis</h2>
            <p className="text-gray-700 leading-relaxed mb-6">
              Europe's electric heavy truck market is experiencing significant regional divergence, with clear 
              leaders emerging in the northwest while southern and eastern European countries lag behind. 
              This geographic pattern reflects varying policy support, infrastructure development, and economic conditions.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Leading Markets</h3>
            <div className="bg-green-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Top Performers by BEV Share:</h4>
              <ul className="space-y-2">
                <li><strong>Switzerland & Norway:</strong> Early adopters with highest penetration rates</li>
                <li><strong>Netherlands:</strong> 11.2% Q3 2025 share, supported by AanZET subsidies</li>
                <li><strong>Sweden:</strong> Strong Nordic adoption pattern</li>
                <li><strong>Austria & Belgium:</strong> Rapid growth with 60%+ YoY increases</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Netherlands: The Success Story</h3>
            <p className="text-gray-700 leading-relaxed mb-4">
              The Netherlands has emerged as Europe's e-truck adoption leader, driven by several key factors:
            </p>
            <ul className="list-disc pl-6 text-gray-700 space-y-2 mb-6">
              <li><strong>AanZET Purchase Subsidies:</strong> Activated in October 2025 for new BEV trucks</li>
              <li><strong>TCO Advantages:</strong> Lower energy costs making BEVs economically competitive</li>
              <li><strong>CO₂-based Tolling:</strong> Starting 2026, favoring zero-emission vehicles</li>
              <li><strong>Infrastructure Development:</strong> Expanding charging network for heavy vehicles</li>
            </ul>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Economic Drivers</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              Total Cost of Ownership (TCO) analysis reveals that BEV trucks are approaching cost parity 
              with diesel on domestic routes in the Netherlands. This economic shift is accelerated by 
              lower energy costs and upcoming regulatory changes that will make diesel operations more expensive.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Future Policy Impact</h3>
            <div className="bg-blue-50 p-6 rounded-lg mb-6">
              <h4 className="font-semibold mb-3">Upcoming EU Policies Supporting E-Truck Adoption:</h4>
              <ul className="space-y-2">
                <li><strong>ETS2 Carbon Pricing:</strong> Making diesel more expensive</li>
                <li><strong>AFIR Infrastructure:</strong> Expanding charging network requirements</li>
                <li><strong>National Subsidies:</strong> Country-specific purchase incentives</li>
                <li><strong>Urban Access Restrictions:</strong> Zero-emission zones in major cities</li>
              </ul>
            </div>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Regional Patterns</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              The data reveals a clear northwest-to-southeast gradient in e-truck adoption. Northern and 
              western European countries benefit from stronger economies, better infrastructure, and more 
              supportive policies, while southern and eastern regions face greater challenges in transitioning 
              to electric heavy vehicles.
            </p>

            <h3 className="text-xl font-semibold text-gray-900 mb-3">Industry Implications</h3>
            <p className="text-gray-700 leading-relaxed mb-6">
              This divergence creates both opportunities and challenges for logistics operators. Companies 
              operating in leading markets can benefit from lower operating costs and regulatory advantages, 
              while those in lagging regions may face competitive disadvantages as the transition accelerates.
            </p>

            <div className="bg-gray-100 p-6 rounded-lg">
              <h4 className="font-semibold mb-2">Source Information:</h4>
              <p className="text-sm text-gray-600">
                Analysis based on ACEA commercial vehicle registration data and market research from Freight Perspectives. 
                <a href="https://www.freightperspectives.com/p/europes-e-truck-market-diverges-a" target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:text-blue-800 underline ml-1">
                  View original source
                </a>
              </p>
            </div>
          </div>
        </article>
      </div>
    </div>
  );
};

export default EuropeTruckArticle;